import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { RequiredMark } from './RequiredMark';

export interface FormSectionProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  description?: string;
  /**
   * Compact density — narrower label column (140px) and side-by-side label/input
   * layout reminiscent of Korean admin forms. Default behaviour stays
   * 'comfortable' so existing pages keep the wide layout until they opt in.
   */
  density?: 'compact' | 'comfortable';
  /**
   * 필수 입력/선택 필드 마커(빨강 `*`)를 라벨 우측에 노출 (ID56).
   * FormSection 이 단일 필드 라벨로 쓰일 때(주로 compact density) 사용.
   */
  required?: boolean;
  /** Optional right-aligned hint / error text below the input area. */
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

export const FormSection = ({
  label,
  description,
  density = 'comfortable',
  required,
  hint,
  error,
  children,
  className,
  ...props
}: FormSectionProps) => {
  if (density === 'compact') {
    return (
      <div
        className={cn('flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4', className)}
        {...props}
      >
        <div
          className="shrink-0 sm:pt-2"
          style={{ width: 'var(--form-label-width)' }}
        >
          <p
            className="text-[13px] font-medium"
            style={{
              color: 'var(--content-strong)',
              lineHeight: 'var(--leading-heading)',
            }}
          >
            {label}
            {required && <RequiredMark />}
          </p>
          {description ? (
            <p
              className="mt-0.5 text-[11px]"
              style={{ color: 'var(--content-muted)' }}
            >
              {description}
            </p>
          ) : null}
        </div>
        <div className="flex-1 min-w-0">
          {children}
          {(hint || error) ? (
            <div className="mt-1 flex justify-end text-[11px]">
              {error ? (
                <span style={{ color: 'var(--signal-danger)' }}>{error}</span>
              ) : (
                <span style={{ color: 'var(--content-muted)' }}>{hint}</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 sm:flex-row sm:gap-8', className)} {...props}>
      <div className="w-full shrink-0 sm:w-[200px] sm:pt-1">
        <p
          className="text-[14px] font-semibold tracking-tight"
          style={{
            color: 'var(--content-strong)',
            lineHeight: 'var(--leading-heading)',
          }}
        >
          {label}
          {required && <RequiredMark />}
        </p>
        {description ? (
          <p className="mt-1 text-[12px] leading-relaxed" style={{ color: 'var(--content-muted)' }}>
            {description}
          </p>
        ) : null}
      </div>
      <div
        className="flex-1 rounded-xl overflow-hidden"
        style={{
          background: 'var(--surface-raised)',
          border: '1px solid var(--divider-strong)',
          boxShadow: 'var(--shadow-card-rest)',
          padding: '20px 24px',
        }}
      >
        {children}
        {(hint || error) ? (
          <div className="mt-2 flex justify-end text-[12px]">
            {error ? (
              <span style={{ color: 'var(--signal-danger)' }}>{error}</span>
            ) : (
              <span style={{ color: 'var(--content-muted)' }}>{hint}</span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
